import { formatDate } from '@privacy-bee/format/date';

/**
 * The calendar day a billing value names, in the `YYYY-MM-DD` shape
 * `@privacy-bee/format` pins to UTC.
 *
 * A renewal date, an invoice date and a quote expiry are calendar days, but the
 * API serializes several of them as instants ("2027-04-27T00:00:00.000000Z").
 * Rendering that instant in the viewer's zone moves it to the 26th everywhere
 * west of UTC — a different billing day than the one the invoice states, for
 * every customer in the United States.
 *
 * A value that does not open with a calendar day is passed through untouched,
 * so `formatDate` still judges it.
 */
function calendarDay(value: string | null | undefined): string {
    if (!value) return '';
    return /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : value;
}

/**
 * A renewal, invoice or expiry date as the customer reads it: "Apr 27, 2027".
 *
 * The day comes from the value itself and the writing comes from `@privacy-bee/format`,
 * which is what keeps one billing day one string in every time zone. An instant
 * whose moment is genuinely the question — when a key was created, when a
 * session was last seen — is not a billing date and calls `formatDate`
 * directly, so it renders in the viewer's own zone.
 *
 * A missing or unparseable value gives `''`: the words a surface shows for a
 * date it does not have are that surface's copy, not this function's.
 */
export function formatBillingDate(value: string | null | undefined): string {
    return formatDate(calendarDay(value));
}

/**
 * How long is left to pay, as an invoice row says it: "3 days left", "Due
 * today", "2 days overdue".
 *
 * The due date is a calendar day (see `calendarDay` above), and "left" is
 * counted against the reader's own calendar day, because that is the day they
 * mean. Both sides are reduced to a day before they are subtracted, so an
 * invoice due tomorrow never reads "0 days left" because the clock happens to
 * be past noon.
 *
 * `null` when the value names no day: the words a row shows for a due date it
 * does not have are that row's copy, not this function's.
 */
export function billingDaysLeft(value: string | null | undefined, now: Date = new Date()): string | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(calendarDay(value));
    if (!match) return null;

    const due = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    const days = Math.round((due - today) / 86_400_000);

    if (days === 0) return 'Due today';
    if (days > 0) return `${days} ${days === 1 ? 'day' : 'days'} left`;

    return `${-days} ${days === -1 ? 'day' : 'days'} overdue`;
}
